import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import EventRegisterModal from './EventRegisterModal';
import { Button } from './ui/button';
import {
  ArrowLeft, MapPin, Calendar, Clock, Loader2, AlertCircle, Image as ImageIcon,
  Users, CheckCircle, IndianRupee, Share2, Clock4,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};
const fmtDateRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return fmtDate(start);
  return `${fmtDate(start)} – ${fmtDate(end)}`;
};

const ACTIVITY_TYPE_TONE = {
  tournament: 'bg-indigo-100 text-indigo-700',
  fitness: 'bg-pink-100 text-pink-700',
  screening: 'bg-amber-100 text-amber-700',
  networking: 'bg-emerald-100 text-emerald-700',
  other: 'bg-gray-100 text-gray-700',
};

const EventDetailPublic = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useCustomerAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registerTarget, setRegisterTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const phoneParam = user?.phone
        ? `?phone=${encodeURIComponent((user.phone || '').replace('+91', '').replace('+', '').replace(/\s/g, ''))}`
        : '';
      const res = await fetch(`${API_URL}/api/events/public/${slug}${phoneParam}`);
      if (!res.ok) throw new Error('Event not found');
      setEvent(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug, user?.phone]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }
  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="h-10 w-10 text-gray-300 mb-2" />
        <p className="text-sm text-gray-600">This event isn't available.</p>
        <Button onClick={() => navigate('/events')} className="mt-4">All Events</Button>
      </div>
    );
  }

  const activities = event.activities || [];
  const viewerRegs = event.viewer_registrations || [];
  // Map of activity_id → first matching viewer registration. The activity
  // card uses this to render the right pill (green Registered / amber Pay now)
  // and we use it inline for the top banner.
  const viewerRegByActivity = viewerRegs.reduce((acc, r) => {
    if (!acc[r.activity_id]) acc[r.activity_id] = r;
    return acc;
  }, {});

  // Resume payment for an abandoned pending registration. Opens Razorpay
  // checkout against a fresh order tied to the same registration row, so the
  // captain doesn't need to re-enter the team roster.
  const handleResumePayment = async (reg) => {
    const phone = (user?.phone || '').replace('+91', '').replace('+', '').replace(/\s/g, '');
    if (!phone) {
      toast({ title: 'Sign in required', description: 'Please sign in with the phone you used to register, then try again.', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/api/events/public/registrations/${reg.registration_id}/resume-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not resume payment');
      }
      const data = await res.json();
      if (data.is_paid) {
        toast({ title: 'Already paid', description: 'This registration is already confirmed.' });
        load();
        return;
      }
      if (!window.Razorpay) {
        throw new Error('Payment library not loaded — refresh the page and try again.');
      }
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: event.venue_name,
        description: `${event.title} — complete payment`,
        order_id: data.order_id,
        prefill: { contact: `+91${phone}`, name: user?.name || '' },
        theme: { color: '#0A2C93' },
        handler: async (response) => {
          try {
            const v = await fetch(
              `${API_URL}/api/events/public/${event.id}/activities/${reg.activity_id}/verify-payment`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              },
            );
            if (!v.ok) {
              const err = await v.json().catch(() => ({}));
              throw new Error(err.detail || 'Payment verification failed');
            }
            toast({ title: 'Payment successful', description: 'Your registration is now confirmed.' });
            load();
          } catch (err) {
            toast({ title: 'Payment verification failed', description: err.message, variant: 'destructive' });
          }
        },
        modal: {
          ondismiss: () => {
            toast({ title: 'Payment cancelled', description: 'You can complete it anytime from this page.' });
          },
        },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      toast({ title: 'Could not start payment', description: e.message, variant: 'destructive' });
    }
  };

  // Share URL routes through /api/share/events/{slug} so social crawlers
  // (WhatsApp/FB/Twitter/LinkedIn/Telegram) get per-event OG tags in the
  // initial HTML response. Humans get an instant client-side redirect back
  // to /events/{slug}. See backend/routes/share_previews.py.
  const shareUrl = (() => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    if (event?.slug) return `${origin}/api/share/events/${event.slug}`;
    return window.location.href;
  })();

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: event.title, url: shareUrl }).catch(() => {});
    } else if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl);
    }
  };

  const whatsappShareUrl = (() => {
    const dateStr = fmtDateRange(event.start_date, event.end_date);
    const text = `Check out ${event.title} at ${event.venue_name} on ${dateStr}! Register here: ${shareUrl}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  })();

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="event-detail-public">
      {/* Top bar */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/events')} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="event-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="flex-1 text-base font-bold truncate">{event.title}</h1>
          <a
            href={whatsappShareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 -mr-1 hover:bg-emerald-50 rounded-lg text-[#25D366]"
            title="Share on WhatsApp"
            data-testid="event-share-whatsapp"
            aria-label="Share on WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M19.05 4.91A9.82 9.82 0 0012.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7zM12.04 20.15h-.01a8.21 8.21 0 01-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 01-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.41 5.82c.01 4.54-3.69 8.23-8.23 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.89 2.38 1.01 2.54.12.17 1.75 2.68 4.24 3.76.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z"/>
            </svg>
          </a>
          <button onClick={handleShare} className="p-2 -mr-2 hover:bg-gray-100 rounded-lg" title="Share" data-testid="event-share">
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Banner */}
      <div className="aspect-video bg-gray-100 max-w-3xl mx-auto sm:rounded-b-2xl overflow-hidden">
        {event.banner_image_url ? (
          <img src={`${API_URL}${event.banner_image_url}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageIcon className="h-16 w-16" />
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Meta */}
        <div className="space-y-1 text-sm text-gray-600">
          <p className="flex items-center gap-2"><Calendar className="h-4 w-4" />{fmtDateRange(event.start_date, event.end_date)}</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{event.venue_name}</p>
          {event.is_multi_activity && (
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-indigo-100 text-indigo-700 mt-1">
              Multi-Activity Event
            </span>
          )}
        </div>

        {/* Viewer status banners — first thing on the page */}
        {viewerRegs.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="viewer-banners">
            {viewerRegs.map(r => {
              const a = activities.find(x => x.id === r.activity_id);
              const isPending = !r.is_paid;
              // Truly free = not paid yet AND zero amount due (genuine free
              // activity). Pending paid registrations have amount_due > 0
              // and must show the amber "complete payment" banner instead
              // of the misleading "Free registration" label.
              const isTrulyFree = r.is_paid && (r.amount_paid || 0) === 0 && (r.amount_due || 0) === 0;
              const tone = isPending
                ? 'bg-amber-50 border-amber-200'
                : 'bg-emerald-50 border-emerald-200';
              const icon = isPending
                ? <Clock4 className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                : <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />;
              const titleTone = isPending ? 'text-amber-900' : 'text-emerald-900';
              const subTone = isPending ? 'text-amber-800' : 'text-emerald-700';
              return (
                <div key={r.registration_id} className={`${tone} border rounded-xl p-3 flex items-start gap-2.5`} data-testid={`viewer-banner-${r.registration_id}`}>
                  {icon}
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${titleTone}`}>
                      {isPending ? (
                        r.registration_type === 'team'
                          ? <>Your team <strong>{r.team_name}</strong> — payment pending</>
                          : (r.quantity || 1) > 1
                            ? <>Your <strong>{r.quantity} tickets</strong> — payment pending</>
                            : 'Your registration — payment pending'
                      ) : (
                        r.registration_type === 'team'
                          ? <>Your team <strong>{r.team_name}</strong> is registered</>
                          : (r.quantity || 1) > 1
                            ? <>You have <strong>{r.quantity} tickets</strong> registered</>
                            : 'You\'re registered'
                      )}
                      {a ? ` for ${a.title}` : ''}
                    </p>
                    <p className={`text-xs ${subTone} mt-0.5`}>
                      {isPending
                        ? <>Complete ₹{Number(r.amount_due || 0).toLocaleString('en-IN')} to confirm your slot.</>
                        : isTrulyFree
                          ? 'Free registration · confirmed'
                          : <>Paid ₹{Number(r.amount_paid).toLocaleString('en-IN')} · confirmed</>}
                    </p>
                    {isPending && (
                      <Button
                        onClick={() => handleResumePayment(r)}
                        className="mt-2 h-9 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`resume-payment-btn-${r.registration_id}`}
                      >
                        Complete payment · ₹{Number(r.amount_due || 0).toLocaleString('en-IN')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {event.status === 'cancelled' && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2.5">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-red-800">Event cancelled</p>
              <p className="text-xs text-red-700 mt-0.5">All registrations have been refunded automatically.</p>
            </div>
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="mt-5 bg-white border rounded-2xl p-4">
            <h2 className="font-semibold text-sm mb-1.5">About</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{event.description}</p>
          </div>
        )}

        {/* Activities */}
        <h2 className="font-bold text-base mt-6 mb-2">
          {event.is_multi_activity ? `Activities (${activities.length})` : 'Registration'}
        </h2>
        <div className="space-y-3" data-testid="activities-list">
          {activities.map(a => (
            <ActivityCard
              key={a.id}
              activity={a}
              eventCancelled={event.status === 'cancelled'}
              viewerReg={viewerRegByActivity[a.id]}
              onResumePayment={handleResumePayment}
              onRegister={() => setRegisterTarget(a)}
            />
          ))}
        </div>
      </div>

      {registerTarget && (
        <EventRegisterModal
          event={event}
          activity={registerTarget}
          onClose={() => setRegisterTarget(null)}
          onRegistered={() => { setRegisterTarget(null); load(); }}
        />
      )}
    </div>
  );
};

const ActivityCard = ({ activity, eventCancelled, viewerReg, onResumePayment, onRegister }) => {
  const filled = activity.current_registrations || 0;
  const max = activity.max_participants || 0;
  const spotsLeft = Math.max(0, max - filled);
  const isFree = (activity.price_per_unit || 0) === 0;
  const isFull = activity.status === 'full' || spotsLeft <= 0;
  const isOpen = activity.status === 'open' && !eventCancelled && !isFull;
  const isTeam = activity.format === 'team';
  // Distinguish: not registered yet / pending payment / confirmed.
  const isRegistered = !!viewerReg;
  const isPendingPayment = isRegistered && !viewerReg.is_paid;

  return (
    <div className="bg-white border rounded-2xl p-4" data-testid={`activity-card-${activity.id}`}>
      <div className="flex items-start gap-2 flex-wrap mb-2">
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${ACTIVITY_TYPE_TONE[activity.activity_type] || ACTIVITY_TYPE_TONE.other}`}>
          {activity.activity_type}
        </span>
        {activity.sport && activity.sport !== 'none' && (
          <span className="text-[10px] font-semibold uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{activity.sport}</span>
        )}
        <span className="text-[10px] font-semibold uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{activity.format}</span>
      </div>
      <h3 className="font-bold text-base text-gray-900 leading-tight">{activity.title}</h3>
      {activity.description && <ActivityDescription text={activity.description} activityId={activity.id} />}

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
        <p className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gray-400" />{fmtDate(activity.date)}</p>
        <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gray-400" />{activity.start_time}–{activity.end_time}</p>
        <p className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-gray-400" />
          {isFull
            ? <span className="font-semibold text-red-600">Full</span>
            : <><span className="font-semibold">{spotsLeft}</span>&nbsp;of {max} {isTeam ? 'team' : 'spot'}{(max !== 1) ? 's' : ''} left</>}
        </p>
        <p className="flex items-center gap-1.5">
          {isFree
            ? <span className="font-bold text-emerald-600">Free</span>
            : <><IndianRupee className="h-3.5 w-3.5 text-gray-400" /><span className="font-bold">{activity.price_per_unit}</span>{isTeam && <span className="text-gray-500">/team</span>}</>}
        </p>
      </div>

      {/* CTA */}
      <div className="mt-4">
        {isPendingPayment ? (
          <Button
            onClick={() => onResumePayment(viewerReg)}
            className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
            data-testid={`pay-now-pill-${activity.id}`}
          >
            <Clock4 className="h-4 w-4" />
            Complete payment · ₹{Number(viewerReg.amount_due || 0).toLocaleString('en-IN')}
          </Button>
        ) : isRegistered ? (
          <div className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-semibold" data-testid={`registered-pill-${activity.id}`}>
            <CheckCircle className="h-4 w-4" />Registered
          </div>
        ) : eventCancelled ? (
          <Button disabled variant="outline" className="w-full">Event cancelled</Button>
        ) : activity.status === 'cancelled' ? (
          <Button disabled variant="outline" className="w-full">Activity cancelled</Button>
        ) : isFull ? (
          <Button disabled variant="outline" className="w-full" data-testid={`activity-full-${activity.id}`}>
            Full — no spots left
          </Button>
        ) : isOpen ? (
          <Button onClick={onRegister} className="w-full h-11" data-testid={`register-btn-${activity.id}`}>
            {isFree ? 'Register for Free' : `Register · ₹${activity.price_per_unit}${isTeam ? '/team' : ''}`}
          </Button>
        ) : (
          <Button disabled variant="outline" className="w-full">{activity.status}</Button>
        )}
      </div>
    </div>
  );
};

const ActivityDescription = ({ text, activityId }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current) {
      setOverflows(ref.current.scrollHeight > ref.current.clientHeight + 1);
    }
  }, [text]);
  return (
    <div className="mt-1.5" data-testid={`activity-description-${activityId}`}>
      <p
        ref={ref}
        className={`text-sm text-gray-700 whitespace-pre-line leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-xs font-semibold text-[#0A2C93] hover:underline mt-1"
          data-testid={`activity-description-toggle-${activityId}`}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

export default EventDetailPublic;
